import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Link as ChakraLink,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Text,
  VStack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  PiSquaresFourDuotone, PiCompassDuotone, PiUsersDuotone, PiUsersThreeDuotone, PiBriefcaseDuotone,
  PiListChecksDuotone, PiCubeDuotone, PiUserPlusDuotone, PiBookOpenDuotone, PiKeyDuotone,
  PiQuestionDuotone, PiInfoDuotone, PiPlusCircleDuotone, PiCheckCircleDuotone, PiLockKeyDuotone, PiSignOutDuotone,
  PiPlusBold, PiListDuotone, PiSealCheckDuotone, PiCaretDownBold, PiCaretUpBold, PiArrowSquareOutDuotone
} from "react-icons/pi";
import { authorization, bindToCommand, getProfile, getMyGroups, getVoterGroups, refreshAuthToken, submitGroupSignup } from "@lubbezposrednio-frontend/core";
import NotificationBell from "../../../components/NotificationBell";
import { SupportActions } from "./SupportActions";
import { DocumentsGate } from "../../DocumentsView/DocumentsGate";

const SIDEBAR_W = "264px";

// hex → rgba (do tintów tła w kolorze sekcji)
const rgba = (hex: string, a: number) => {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};
const EASE = "all 0.16s cubic-bezier(0.22, 1, 0.36, 1)";

type NavItem = { label: string; to: string; icon: React.ElementType; match?: string[]; external?: boolean; sekcja?: string; color?: string };
type NavSection = { title?: string; items: NavItem[] };
type QuickGroup = { id: string; name: string; avatarUrl?: string };

const EXPLORER_URL = process.env.REACT_APP_EXPLORER_URL || "https://eksplorer.lubbezposrednio.pl";

// Menu pokategoryzowane (mini-nagłówki sekcji).
const NAV_MAIN: NavItem[] = [
  { label: "Pulpit", to: "/organizator", icon: PiSquaresFourDuotone, color: "#430098" },
  { label: "Społeczność", to: "/organizator/odkrywaj", icon: PiCompassDuotone, sekcja: "spolecznosc", color: "#7c3aed" },
  { label: "Ludzie", to: "/organizator/odkrywaj?sekcja=ludzie", icon: PiUsersDuotone, sekcja: "ludzie", color: "#0ea5e9" },
  { label: "Organizacje", to: "/organizator/odkrywaj?sekcja=organizacje", icon: PiBriefcaseDuotone, sekcja: "organizacje", color: "#6366f1" }
];
const NAV_VOTES: NavSection = {
  title: "Głosowania",
  items: [{ label: "Głosowania", to: "/organizator/glosowania", icon: PiListChecksDuotone, color: "#2563eb", match: ["/organizator/twoje-glosowania", "/organizator/udzial", "/aktywne-glosowania"] }]
};
const NAV_GROUPS_ITEM: NavItem = { label: "Grupy", to: "/organizator/grupy", icon: PiUsersThreeDuotone, color: "#10b981", match: ["/organizator/grupy-feed", "/organizator/moje-grupy"] };
const NAV_TOOLS: NavSection = {
  title: "Więcej",
  items: [{ label: "Eksplorer bloków", to: EXPLORER_URL, icon: PiCubeDuotone, external: true, color: "#0d9488" }]
};

// Strony statyczne — zwinięte na dole sidebaru.
const HELP_SECTION: NavSection = {
  title: "Pomoc",
  items: [
    { label: "Jak stworzyć głosowanie", to: "/jak-stworzyc-glosowanie", icon: PiPlusCircleDuotone, color: "#64748b" },
    { label: "Jak zagłosować", to: "/jak-zaglosowac", icon: PiCheckCircleDuotone, color: "#64748b" },
    { label: "Tajne głosowanie ZKP", to: "/tajne-glosowanie-zkp", icon: PiLockKeyDuotone, color: "#64748b" },
    { label: "FAQ", to: "/faq", icon: PiQuestionDuotone, color: "#64748b" },
    { label: "O nas", to: "/o-nas", icon: PiInfoDuotone, color: "#64748b" }
  ]
};

const DEV_SECTION: NavSection = {
  title: "Deweloper",
  items: [
    { label: "Dokumentacja", to: "/organizator/dokumentacja", icon: PiBookOpenDuotone, color: "#6366f1" },
    { label: "Klucz API", to: "/organizator/api", icon: PiKeyDuotone, color: "#f59e0b" }
  ]
};

const isActive = (pathname: string, search: string, item: NavItem) => {
  if (item.to === "/organizator") return pathname === "/organizator";
  // Społeczność / Ludzie / Organizacje — ta sama trasa /organizator/odkrywaj, rozróżniane po ?sekcja=
  if (item.to.startsWith("/organizator/odkrywaj")) {
    if (!pathname.startsWith("/organizator/odkrywaj")) return false;
    const sek = new URLSearchParams(search).get("sekcja") || "spolecznosc";
    return sek === (item.sekcja || "spolecznosc");
  }
  return [item.to, ...(item.match ?? [])].some(p => pathname.startsWith(p));
};

const NavRow: React.FC<{ item: NavItem; active: boolean; onNavigate?: () => void }> = ({ item, active, onNavigate }) => {
  const c = item.color || "#430098";
  const linkProps: Record<string, unknown> = item.external
    ? { as: ChakraLink, href: item.to, isExternal: true }
    : { as: Link, to: item.to };
  return (
    <HStack
      {...linkProps}
      onClick={onNavigate}
      spacing="13px"
      px="12px"
      py="9px"
      borderRadius="12px"
      bg={active ? "rgba(24,12,48,0.06)" : "transparent"}
      transition={EASE}
      _hover={{ bg: active ? "rgba(24,12,48,0.08)" : "rgba(24,12,48,0.035)", textDecoration: "none" }}
    >
      <Icon as={item.icon} boxSize="21px" color={c} flexShrink={0} />
      <Text fontSize="14.5px" fontWeight={active ? "800" : "600"} color={active ? "#1b1030" : "#4a4458"} noOfLines={1}>
        {item.label}
      </Text>
      {item.external && <Icon as={PiArrowSquareOutDuotone} boxSize="13px" color="gray.400" ml="auto" />}
    </HStack>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.1em" color="#aaa3b9" mb="8px" mt="2px" px="12px">
    {children}
  </Text>
);

const Section: React.FC<{ section: NavSection; pathname: string; search: string; onNavigate?: () => void }> = ({ section, pathname, search, onNavigate }) => (
  <Box>
    {section.title && <SectionLabel>{section.title}</SectionLabel>}
    <VStack align="stretch" spacing="3px">
      {section.items.map(item => (
        <NavRow key={item.to} item={item} active={isActive(pathname, search, item)} onNavigate={onNavigate} />
      ))}
    </VStack>
  </Box>
);

// Split-button „Utwórz głosowanie" + caret z wyborem Głosowanie / Grupę.
const CreateMenu: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  return (
    <Flex w="100%" borderRadius="14px" overflow="hidden" boxShadow="0 8px 20px -8px rgba(229,31,68,0.55)">
      <Button
        as={Link}
        to="/organizator/utworz-glosowanie"
        onClick={onNavigate}
        leftIcon={<PiPlusBold />}
        bg="accent"
        color="white"
        fontWeight="800"
        fontSize="14.5px"
        letterSpacing="-0.01em"
        borderRadius="0"
        h="46px"
        flex="1"
        minW={0}
        boxShadow="none"
        transition={EASE}
        _hover={{ bg: "#d21b3f", textDecoration: "none" }}
        _active={{ bg: "#b8172f" }}
      >
        Utwórz głosowanie
      </Button>
      <Menu placement="bottom-end">
        <MenuButton
          as={IconButton}
          aria-label="Więcej opcji tworzenia"
          icon={<PiCaretDownBold />}
          bg="#cf1c3e"
          color="white"
          borderRadius="0"
          h="46px"
          w="44px"
          minW="44px"
          boxShadow="none"
          transition={EASE}
          _hover={{ bg: "#b8172f" }}
          _active={{ bg: "#a3142a" }}
        />
        <Portal>
        <MenuList className="cta-menu" borderRadius="16px" py="7px" minW="216px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 18px 44px -12px rgba(43,0,90,0.35)" zIndex={1600}>
          <MenuItem as={Link} to="/organizator/utworz-glosowanie" onClick={onNavigate} borderRadius="10px" mx="6px" w="auto" icon={<Flex w="28px" h="28px" borderRadius="9px" bg={rgba("#2563eb", 0.12)} color="#2563eb" align="center" justify="center"><Icon as={PiListChecksDuotone} boxSize="16px" /></Flex>} fontWeight="700" fontSize="14px">
            Nowe głosowanie
          </MenuItem>
          <MenuItem as={Link} to="/organizator/grupy/nowa" onClick={onNavigate} borderRadius="10px" mx="6px" w="auto" icon={<Flex w="28px" h="28px" borderRadius="9px" bg={rgba("#10b981", 0.12)} color="#10b981" align="center" justify="center"><Icon as={PiUserPlusDuotone} boxSize="16px" /></Flex>} fontWeight="700" fontSize="14px">
            Nowa grupa
          </MenuItem>
        </MenuList>
        </Portal>
      </Menu>
    </Flex>
  );
};

// „Kilka grupek ze zdjęciami" — szybki dostęp do grup użytkownika (pod pozycją „Grupy").
const QuickGroups: React.FC<{ groups: QuickGroup[]; pathname: string; onNavigate?: () => void }> = ({ groups, pathname, onNavigate }) => {
  if (!groups.length) return null;
  return (
    <VStack align="stretch" spacing="2px" pl="16px" mt="2px">
      {groups.map(g => {
        const active = pathname.startsWith(`/organizator/grupy/${g.id}`);
        return (
          <HStack
            key={g.id}
            as={Link}
            to={`/organizator/grupy/${g.id}`}
            onClick={onNavigate}
            spacing="9px"
            px="8px"
            py="6px"
            borderRadius="10px"
            bg={active ? rgba("#10b981", 0.1) : "transparent"}
            transition={EASE}
            _hover={{ bg: active ? rgba("#10b981", 0.12) : "rgba(20,10,40,0.04)", textDecoration: "none" }}
          >
            <Avatar size="2xs" w="24px" h="24px" name={g.name} src={g.avatarUrl} bg="#10b981" color="white" borderRadius="8px" fontWeight="800" />
            <Text fontSize="13px" fontWeight={active ? "700" : "600"} color={active ? "#0b7355" : "#5a5468"} noOfLines={1}>
              {g.name}
            </Text>
          </HStack>
        );
      })}
      <ChakraLink as={Link} to="/organizator/grupy" onClick={onNavigate} fontSize="12px" fontWeight="700" color="#10b981" px="8px" py="5px" _hover={{ textDecoration: "none", color: "#0b7355" }}>
        Wszystkie grupy →
      </ChakraLink>
    </VStack>
  );
};

const CollapsibleSection: React.FC<{ section: NavSection; pathname: string; search: string; onNavigate?: () => void }> = ({ section, pathname, search, onNavigate }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box>
      <HStack as="button" type="button" onClick={() => setOpen(o => !o)} w="100%" justify="space-between" px="12px" py="7px" borderRadius="10px" transition={EASE} _hover={{ bg: "rgba(20,10,40,0.04)" }}>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.1em" color="#aaa3b9">
          {section.title}
        </Text>
        <Icon as={open ? PiCaretUpBold : PiCaretDownBold} boxSize="14px" color="#aaa3b9" transition={EASE} />
      </HStack>
      {open && (
        <VStack align="stretch" spacing="3px" mt="4px">
          {section.items.map(item => (
            <NavRow key={item.to} item={item} active={isActive(pathname, search, item)} onNavigate={onNavigate} />
          ))}
        </VStack>
      )}
    </Box>
  );
};

const SidebarContent: React.FC<{ userName: string; avatarUrl?: string; apiAccess: boolean; profileRef?: string; verified?: boolean; quickGroups: QuickGroup[]; onNavigate?: () => void }> = observer(
  ({ userName, avatarUrl, apiAccess, profileRef, verified, quickGroups, onNavigate }) => {
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const logOutCommand = useMemo(() => authorization.logOut(toast, navigate), [toast, navigate]);

    return (
      <Flex direction="column" h="100%" bg="transparent" overflowY="hidden">
        <Box px="16px" pt="16px" flexShrink={0}>
          <Flex align="center" mb="18px" pt="2px" px="4px">
            <Link to="/organizator" onClick={onNavigate} aria-label="Pulpit">
              <Image src="/LBP_Logo_Black.svg" alt="lubbezpośrednio.pl" h="30px" />
            </Link>
          </Flex>
          <CreateMenu onNavigate={onNavigate} />
        </Box>

        <Box flex="1" overflowY="auto" px="16px" pt="18px" pb="6px">
          <VStack align="stretch" spacing="16px">
          <VStack align="stretch" spacing="3px">
            <NotificationBell variant="nav" />
            {NAV_MAIN.map(item => (
              <NavRow key={item.to} item={item} active={isActive(pathname, search, item)} onNavigate={onNavigate} />
            ))}
          </VStack>
          <Section section={NAV_VOTES} pathname={pathname} search={search} onNavigate={onNavigate} />
          <Box>
            <SectionLabel>Twoje grupy</SectionLabel>
            <VStack align="stretch" spacing="3px">
              <NavRow item={NAV_GROUPS_ITEM} active={isActive(pathname, search, NAV_GROUPS_ITEM)} onNavigate={onNavigate} />
              <QuickGroups groups={quickGroups} pathname={pathname} onNavigate={onNavigate} />
            </VStack>
          </Box>
          <Box>
            <Section section={NAV_TOOLS} pathname={pathname} search={search} onNavigate={onNavigate} />
            <SupportActions apiAccess={apiAccess} />
          </Box>
          </VStack>

          <Box mt="12px">
            {apiAccess && <CollapsibleSection section={DEV_SECTION} pathname={pathname} search={search} onNavigate={onNavigate} />}
            <CollapsibleSection section={HELP_SECTION} pathname={pathname} search={search} onNavigate={onNavigate} />
          </Box>
        </Box>

        <Box px="16px" pt="8px" pb="16px" flexShrink={0}>
          <Box
            p="8px"
            borderRadius="16px"
            sx={{
              background: "rgba(255,255,255,0.5)",
              border: "1px solid rgba(255,255,255,0.65)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)"
            }}
          >
          <HStack spacing="6px">
            <HStack
              as={Link}
              to={profileRef ? `/profil/${profileRef}` : "/organizator/konto"}
              onClick={onNavigate}
              flex="1"
              minW={0}
              spacing="11px"
              px="6px"
              py="6px"
              borderRadius="12px"
              transition={EASE}
              _hover={{ bg: "rgba(24,12,48,0.05)", textDecoration: "none" }}
            >
              <Box position="relative" flexShrink={0}>
                <Avatar size="sm" name={userName || "?"} src={avatarUrl || undefined} bg="primary" color="white" fontWeight="800" />
                {verified && (
                  <Flex position="absolute" bottom="-2px" right="-2px" w="17px" h="17px" bg="#1d9bf0" borderRadius="full" align="center" justify="center" border="2.5px solid white" title="Konto zweryfikowane">
                    <Icon as={PiSealCheckDuotone} boxSize="10px" color="white" />
                  </Flex>
                )}
              </Box>
              <Box minW={0} flex="1">
                <Text fontSize="14px" fontWeight="800" color="#1b1030" noOfLines={1}>
                  {userName || "Twoje konto"}
                </Text>
                <Text fontSize="11.5px" color={verified ? "#7a7488" : "accent"} fontWeight={verified ? "600" : "700"} noOfLines={1}>
                  {verified ? (profileRef ? "Zobacz profil publiczny" : apiAccess ? "Konto · API" : "Konto") : "Zweryfikuj konto"}
                </Text>
              </Box>
            </HStack>
            <IconButton
              aria-label="Wyloguj"
              icon={<Icon as={PiSignOutDuotone} boxSize="21px" />}
              variant="ghost"
              color="#6a6478"
              h="40px"
              w="40px"
              minW="40px"
              borderRadius="12px"
              flexShrink={0}
              transition={EASE}
              _hover={{ bg: "rgba(229,31,68,0.1)", color: "accent" }}
              {...bindToCommand(logOutCommand)}
            />
          </HStack>
          </Box>
          <SidebarFooterLinks onNavigate={onNavigate} />
        </Box>
      </Flex>
    );
  }
);

// Rozwijane linki prawne — w stopce lewego sidebaru (footer panelu przeniesiony tutaj, żeby nie psuł wysokości).
const SidebarFooterLinks: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const linkSx = { fontSize: "12px", color: "#8a8499", fontWeight: "600", py: "3px", _hover: { color: "accent", textDecoration: "none" } } as const;
  return (
    <Box mt="8px">
      <HStack as="button" type="button" onClick={() => setOpen(o => !o)} w="100%" justify="center" spacing="6px" py="6px" borderRadius="10px" transition={EASE} _hover={{ bg: "rgba(24,12,48,0.035)" }}>
        <Text fontSize="11px" fontWeight="700" color="#9a94ab" textTransform="uppercase" letterSpacing="0.08em">Informacje</Text>
        <Icon as={open ? PiCaretUpBold : PiCaretDownBold} boxSize="12px" color="#9a94ab" transition={EASE} />
      </HStack>
      {open && (
        <VStack align="stretch" spacing="1px" mt="4px" px="6px" pb="4px">
          <ChakraLink as={Link} to="/regulamin" onClick={onNavigate} {...linkSx}>Regulamin</ChakraLink>
          <ChakraLink as={Link} to="/polityka-prywatnosci" onClick={onNavigate} {...linkSx}>Polityka prywatności</ChakraLink>
          <ChakraLink as={Link} to="/deklaracja-dostepnosci" onClick={onNavigate} {...linkSx}>Deklaracja dostępności</ChakraLink>
          <ChakraLink as={Link} to="/faq" onClick={onNavigate} {...linkSx}>Pomoc</ChakraLink>
          <Text fontSize="10.5px" color="#b3adc0" mt="4px">© {new Date().getFullYear()} lubbezpośrednio.pl</Text>
        </VStack>
      )}
    </Box>
  );
};

/**
 * Padded content container for organizer pages. Optional `header` is rendered
 * above the padded body — e.g. a PageHeader card.
 */
export const OrganizerPage: React.FC<{ children: React.ReactNode; header?: React.ReactNode }> = ({ children, header }) => (
  <>
    {header}
    <Box w="100%" maxW="1240px" mx="auto" px={{ base: "20px", md: "40px", xl: "56px" }} py={{ base: "24px", md: "36px" }}>
      {children}
    </Box>
  </>
);

// Cache shellu (profil + szybkie grupy). Shell montuje się w DWÓCH miejscach (OrganizerLayout dla
// /organizator/* i AdaptiveShell dla /odkrywaj itd.) — bez cache'a każde przejście między nimi
// remountuje sidebar i ponownie ładuje grupy (widoczny „flash"). Cache trzyma dane między remountami.
type ShellData = { userName: string; avatarUrl: string; apiAccess: boolean; profileRef: string; verified: boolean; quickGroups: QuickGroup[] };
let shellCache: ShellData | null = null;
const EMPTY_SHELL: ShellData = { userName: "", avatarUrl: "", apiAccess: false, profileRef: "", verified: false, quickGroups: [] };

/** Persistent shell: fixed sidebar (desktop) / topbar + drawer (mobile) + content area + footer. */
export const OrganizerShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState<ShellData>(() => shellCache ?? EMPTY_SHELL);
  const { userName, avatarUrl, apiAccess, profileRef, verified, quickGroups } = data;

  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const abs = (u?: string | null) => (u ? `${backend}${u}` : undefined);

    const load = async () => {
      // cold-boot na publicznej trasie nie odświeża tokenu jak organizerPathsLoader — zrób to, by getProfile nie poległo na 401
      const tokenExpiry = Number(localStorage.getItem("tokenExpiryDate"));
      if (tokenExpiry && tokenExpiry < Date.now()) {
        await refreshAuthToken();
      }
      const next: ShellData = { ...(shellCache ?? EMPTY_SHELL) };
      let myEmail: string | undefined;
      try {
        const r = await getProfile();
        const u = r.data?.user;
        myEmail = u?.email;
        next.userName = u?.is_organization ? u?.organization_name || "" : u?.first_name || "";
        next.avatarUrl = abs(u?.avatar_url) || "";
        next.apiAccess = !!u?.api_access;
        next.profileRef = u?.profile_public && u?.profile_ref ? u.profile_ref : "";
        next.verified = !!u?.verified;
      } catch {
        /* brak profilu nie blokuje widoku */
      }
      // Auto-dołączenie do grupy po rejestracji/logowaniu z zaproszenia (token zapamiętany w GroupSignupView).
      const pendingJoin = localStorage.getItem("pendingGroupJoinToken");
      if (pendingJoin && myEmail) {
        localStorage.removeItem("pendingGroupJoinToken");
        localStorage.removeItem("pendingGroupJoinName");
        try {
          const jr = await submitGroupSignup(pendingJoin, { email: myEmail, consent: true });
          toast({
            title: jr.data?.confirmed ? "Dołączono do grupy z zaproszenia!" : "Wysłaliśmy potwierdzenie zapisu do grupy.",
            status: "success",
            duration: 4000,
            isClosable: true
          });
          // Po dołączeniu prowadź użytkownika prosto do jego grup (akcja widoczna, nie cicha).
          navigate("/organizator/grupy?w=naleze");
        } catch {
          /* token mógł wygasnąć — ciche */
        }
      }
      // Szybkie grupy (member + zarządzane) — ciche, nie blokują shellu.
      try {
        const [mine, owned] = await Promise.allSettled([getMyGroups(), getVoterGroups()]);
        const list: QuickGroup[] = [];
        const seen = new Set<string>();
        const push = (id: string, name: string, avatarUrl?: string | null) => {
          if (seen.has(id)) return;
          seen.add(id);
          list.push({ id, name, avatarUrl: abs(avatarUrl) });
        };
        if (mine.status === "fulfilled") mine.value.data?.voter_groups?.forEach(g => push(g.id, g.name, g.avatar_url));
        if (owned.status === "fulfilled") owned.value.data?.voter_groups?.forEach(g => push(g.id, g.name, g.avatar_url));
        next.quickGroups = list.slice(0, 5);
      } catch {
        /* brak grup nie blokuje shellu */
      }
      shellCache = next;
      setData(next);
    };
    load();
  }, []);

  return (
    <Flex minH="100vh" position="relative" sx={{ background: "linear-gradient(160deg, #F6F6F7 0%, #F0F0F2 55%, #EAEAEC 100%)" }}>
      <DocumentsGate />
      <Box
        display={{ base: "none", lg: "block" }}
        position="fixed"
        left="0"
        top="0"
        h="100vh"
        w="288px"
        overflow="hidden"
        zIndex={100}
        borderRight="1px solid rgba(24,12,48,0.07)"
      >
        <SidebarContent userName={userName} avatarUrl={avatarUrl} apiAccess={apiAccess} profileRef={profileRef} verified={verified} quickGroups={quickGroups} />
      </Box>

      <Flex direction="column" flex="1" minW={0} w="100%" ml={{ base: 0, lg: "288px" }} position="relative" zIndex={1}>
        <Flex display={{ base: "flex", lg: "none" }} position="sticky" top="0" zIndex={90} h="64px" bg="white" px="20px" align="center" justify="space-between" borderBottom="1px solid rgba(67,0,152,0.08)">
          <Link to="/organizator" aria-label="Pulpit">
            <Image src="/LBP_Logo_Black.svg" alt="logo" h="28px" />
          </Link>
          <HStack spacing="2px">
            <NotificationBell />
            <IconButton aria-label="Menu" icon={<PiListDuotone size={24} />} onClick={onOpen} variant="ghost" color="primary" />
          </HStack>
        </Flex>

        <Box flex="1" minW={0}>{children}</Box>
      </Flex>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent maxW={SIDEBAR_W}>
          <SidebarContent userName={userName} avatarUrl={avatarUrl} apiAccess={apiAccess} profileRef={profileRef} verified={verified} quickGroups={quickGroups} onNavigate={onClose} />
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};

/** Route element: wraps all /organizator child routes in the shell. */
export const OrganizerLayout: React.FC = () => (
  <OrganizerShell>
    <Outlet />
  </OrganizerShell>
);
